/**
 * Player Connection Analytics
 *
 * Standalone analytics functions for connection and system-level player events.
 * These events pull directly from global state stores rather than going through
 * the QuickstartAnalyticsManager (which is instance-based and quickstart-scoped).
 *
 * This module mirrors the pattern of legacyIpcAnalyticsEvents.ts on the host side.
 *
 * Events covered:
 *  - Invite Viewed            (fires when joinState transitions to 'chooseInvite')
 *  - Invite Accepted          (fires when joinState transitions to 'connecting')
 *  - Player Framerate Panicked (fires when the peer quality manager detects a panic)
 *  - WebRTC Stats Gathered    (fires when batched WebRTC quality stats are flushed)
 */

import {
  sendAnalytics,
  PlaycastAnalyticsPlayerInviteViewed,
  PlaycastAnalyticsPlayerInviteAccepted,
  PlaycastAnalyticsPlayerFrameratePanicked,
  PlaycastAnalyticsWebRtcStatsGathered,
} from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '..';
import { authStore, joinState } from '../stores/globalStores';
import { invitationState, playerNameState } from '../stores/lobbyStores';
import { playerConnectionState, truncMachineIdState } from '../stores/playerStores';
import type { WebrtcQualityStatsMetric } from '../types/webrtcTypes';

// ============================================================================
// Invite Analytics
// ============================================================================

const sendInviteViewedEvent = (): void => {
  const playerAuthId = authStore.currentState.playerAuthId;
  const invitationId = invitationState.currentState.selectedInvite?.id ?? '';
  const persistentId = getPersistentPlayerId();

  sendAnalytics({
    name: 'Invite Viewed',
    role: 'player',
    playerAuthId,
    persistentId,
    invitationId,
  } as PlaycastAnalyticsPlayerInviteViewed);
};

const sendInviteAcceptedEvent = (): void => {
  const invitationId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerAuthId = authStore.currentState.playerAuthId;
  const playerName = playerNameState.currentState.name;
  const persistentId = getPersistentPlayerId();

  sendAnalytics({
    name: 'Invite Accepted',
    role: 'player',
    playerAuthId,
    persistentId,
    playerName,
    invitationId,
  } as PlaycastAnalyticsPlayerInviteAccepted);
};

/**
 * Sets up the joinState listener that fires invite analytics at the appropriate
 * state transitions. Call once during connection event initialization.
 */
export const initInviteAnalyticsListeners = (): void => {
  joinState.addListener((state, previousState) => {
    if (state.state === 'chooseInvite' && state.state !== previousState?.state) {
      sendInviteViewedEvent();
    }
    if (state.state === 'connecting' && state.state !== previousState?.state) {
      sendInviteAcceptedEvent();
    }
  });
};

// ============================================================================
// Peer Quality Analytics
// ============================================================================

export const sendPlayerFrameratePanickedEvent = (hardReset: boolean, reason: 'fps' | 'bitrate'): void => {
  const broadcastSessionId = playerConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStore.currentState.playerAuthId ?? '';
  const invitationId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = playerConnectionState.currentState.playerPeerId ?? '';
  const persistentId = getPersistentPlayerId();

  sendAnalytics({
    name: 'Player Framerate Panicked',
    role: 'system',
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    invitationId,
    hardReset,
    reason,
  } as PlaycastAnalyticsPlayerFrameratePanicked);
};

export const sendWebRtcStatsGatheredEvent = (stats: WebrtcQualityStatsMetric[], interval: number): void => {
  const sanitizedStats = stats.map((stat) => {
    const sanitizedRawStats = { ...stat.rawStats };
    Object.keys(sanitizedRawStats).forEach((key) => {
      if (typeof sanitizedRawStats[key] === 'number' && isNaN(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = 0;
      }
      if (typeof sanitizedRawStats[key] === 'object' && sanitizedRawStats[key] !== null) {
        Object.keys(sanitizedRawStats[key]).forEach((subKey) => {
          if (typeof sanitizedRawStats[key][subKey] === 'number' && isNaN(sanitizedRawStats[key][subKey])) {
            sanitizedRawStats[key][subKey] = 0;
          }
        });
      }
      if (Array.isArray(sanitizedRawStats[key])) {
        sanitizedRawStats[key] = sanitizedRawStats[key].map((val) => {
          if (typeof val === 'number' && isNaN(val)) return 0;
          return val;
        });
      }
    });
    return { ...stat, rawStats: sanitizedRawStats };
  });

  const broadcastSessionId = playerConnectionState.currentState.broadcastSessionId;
  const playerAuthId = authStore.currentState.playerAuthId ?? '';
  const invitationId = invitationState.currentState.selectedInvite?.id ?? '';
  const playerConnectionId: string = playerConnectionState.currentState.playerPeerId ?? '';
  const persistentId = getPersistentPlayerId();

  sendAnalytics({
    name: 'WebRTC Stats Gathered',
    role: 'system',
    broadcastSessionId,
    playerAuthId,
    persistentId,
    playerConnectionId,
    invitationId,
    hostMachineId: truncMachineIdState.currentState || undefined,
    qualityStats: sanitizedStats,
    collectionInterval: interval,
  } as PlaycastAnalyticsWebRtcStatsGathered);
};
